import { SubtitleDragHandle, type SubtitleDragPosition } from '@/components/subtitles/SubtitleDragHandle';
import { WatermarkDragHandle, type WatermarkDragHandleProps } from '@/components/watermark/WatermarkDragHandle';
import SatisfyingCellEditor from '@/components/layers/SatisfyingCellEditor';
import HeadlineBoxEditor from '@/components/layouts/HeadlineBoxEditor';
import LayoutAnchorEditor from '@/components/layouts/LayoutAnchorEditor';
import {
  canDragLayoutHeadline,
  defaultHeadlineDrag,
  resolveHeadlineAnchor,
} from '@/lib/layouts/headlineStyle';
import type { PostLayoutConfig } from '@/lib/layouts/types';

interface PreviewChromeEditorsProps {
  subtitlesEnabled: boolean;
  subtitlePosition: string;
  subtitleMarginVertical: number;
  onSubtitleChange: (next: SubtitleDragPosition) => void;
  postLayout: PostLayoutConfig | null;
  layoutLocked?: boolean;
  isLandscape?: boolean;
  onPostLayoutPatch: (patch: Record<string, unknown>) => void;
  /** Free-plan watermark drag target; omit/null when no default watermark shows. */
  watermark?: WatermarkDragHandleProps | null;
}

/** Subtitle + layout chrome drag handles used by the editor preview and the
 *  clips-page Estilizar player. Layers stay editor-only. */
export function PreviewChromeEditors({
  subtitlesEnabled,
  subtitlePosition,
  subtitleMarginVertical,
  onSubtitleChange,
  postLayout,
  layoutLocked = false,
  isLandscape = false,
  onPostLayoutPatch,
  watermark,
}: PreviewChromeEditorsProps) {
  return (
    <>
      {watermark && <WatermarkDragHandle {...watermark} />}
      {subtitlesEnabled && postLayout?.kind !== 'circulo' && (
        <SubtitleDragHandle
          position={subtitlePosition}
          marginVertical={subtitleMarginVertical}
          onChange={onSubtitleChange}
        />
      )}
      {postLayout?.kind === 'satisfatorio' && !layoutLocked && (
        <SatisfyingCellEditor
          config={postLayout}
          isLandscape={isLandscape}
          onConfigPatch={(patch) => onPostLayoutPatch(patch)}
        />
      )}
      {postLayout?.kind === 'cortes' && !layoutLocked && (
        <HeadlineBoxEditor
          config={postLayout}
          onConfigPatch={(patch) => onPostLayoutPatch(patch)}
        />
      )}
      {canDragLayoutHeadline(postLayout) && !layoutLocked && postLayout && (
        <LayoutAnchorEditor
          key={postLayout.kind}
          anchor={resolveHeadlineAnchor(postLayout)}
          width={defaultHeadlineDrag(postLayout).width}
          axis={defaultHeadlineDrag(postLayout).axis}
          onChange={(headlineAnchor) => {
            if (postLayout.kind === 'cortes') return;
            onPostLayoutPatch({ headlineAnchor });
          }}
        />
      )}
    </>
  );
}
